import type {
  AgentAskKind,
  AgentConversationMessage,
  AgentConversationSummary,
  AgentConversationTurn,
  AgentSendConversationMessageResponse,
  PendingAgentAsk,
} from "../api/agent-conversation-client"
import type {
  RobinActiveAsk,
  RobinChoice,
  RobinChoicesAttachment,
  RobinConversation,
  RobinMessage,
  LiveContextRef,
} from "../robin-types"

export const BACKEND_CONVERSATION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isBackendConversationId(value: string): boolean {
  return BACKEND_CONVERSATION_ID_PATTERN.test(value)
}

export function agentConversationToRobin(
  conversation: AgentConversationSummary,
  nowMs: number,
): RobinConversation {
  return {
    id: conversation.id,
    title: conversation.title || "New chat",
    status: "idle",
    updatedLabel: labelFromDate(conversation.updated_at, nowMs),
    messages: [],
    hydrated: false,
    hasHistory: true,
    activeAsk: null,
  }
}

export function agentMessageToRobin(message: AgentConversationMessage): RobinMessage {
  const fileAttachments =
    message.attachments?.filter((attachment) => attachment.id && attachment.fileName) ?? []

  return {
    id: message.id,
    role: message.author_type === "bot" ? "assistant" : "user",
    text: message.body,
    ...(fileAttachments.length ? { fileAttachments } : {}),
    ...(message.mutationReceipt ? { receipt: message.mutationReceipt } : {}),
  }
}

export function pendingAskFromFinalResponse(
  conversationId: string,
  final: AgentSendConversationMessageResponse,
): PendingAgentAsk | null {
  if (final.response.type !== "ask_user" || !final.botMessage) return null
  return {
    askUserToolCallId: final.response.askUserToolCallId,
    botMessageId: final.botMessage.id,
    kind: final.response.kind,
    options: final.response.options,
    createdAt: final.botMessage.created_at,
    conversationId,
  }
}

export function choicesAttachmentFromAsk(
  conversationId: string,
  ask: PendingAgentAsk,
): RobinChoicesAttachment {
  return {
    type: "choices",
    id: ask.askUserToolCallId,
    options: ask.options.map((option): RobinChoice => ({
      id: String(option.index),
      name: option.label,
      meta: optionKindLabel(option.kind),
    })),
    live: {
      conversationId,
      askUserToolCallId: ask.askUserToolCallId,
      kind: ask.kind,
    },
  }
}

export function activeAskToRobin(ask: PendingAgentAsk | null): RobinActiveAsk | null {
  if (!ask) return null
  return {
    conversationId: ask.conversationId,
    askUserToolCallId: ask.askUserToolCallId,
    kind: ask.kind,
    options: ask.options.map((option): RobinChoice => ({
      id: String(option.index),
      name: option.label,
      meta: optionKindLabel(option.kind),
    })),
  }
}

/**
 * Rehydrates backend asks into visible choices cards while keeping stale or
 * already-resolved asks from taking over the composer. The active ask is always
 * chosen from unsuppressed asks that actually attached to a visible bot message.
 */
export function mergePendingAsksIntoMessages(
  conversationId: string,
  messages: RobinMessage[],
  pendingAsks: PendingAgentAsk[],
  suppressedAskIds: ReadonlySet<string>,
): { messages: RobinMessage[]; activeAsk: PendingAgentAsk | null } {
  const sortedAsks = pendingAsks
    .filter((ask) => !suppressedAskIds.has(ask.askUserToolCallId))
    .sort(comparePendingAsk)
  const latestAskByMessageId = new Map<string, PendingAgentAsk>()

  for (const ask of sortedAsks) {
    latestAskByMessageId.set(ask.botMessageId, ask)
  }

  const attachedAskIds = new Set<string>()
  const nextMessages = messages.map((message) => {
    const ask = latestAskByMessageId.get(message.id)
    if (!ask) return message

    const attachment = message.attachment
    if (attachment?.type === "choices" && attachment.resolved) {
      return message
    }

    attachedAskIds.add(ask.askUserToolCallId)
    return {
      ...message,
      attachment: choicesAttachmentFromAsk(conversationId, ask),
    }
  })

  const activeAsk =
    [...sortedAsks]
      .reverse()
      .find((ask) => attachedAskIds.has(ask.askUserToolCallId)) ?? null

  return { messages: nextMessages, activeAsk }
}

export function suppressAskInMessages(
  messages: RobinMessage[],
  askUserToolCallId: string,
): RobinMessage[] {
  return messages.map((message) => {
    const attachment = message.attachment
    if (
      attachment?.type !== "choices" ||
      attachment.resolved ||
      attachment.live?.askUserToolCallId !== askUserToolCallId
    ) {
      return message
    }
    return {
      ...message,
      attachment: { ...attachment, readOnly: true, live: undefined },
    }
  })
}

/**
 * Closes a backend ask everywhere the transcript can still remember it. This is
 * what keeps a successfully answered freeform ask from being rediscovered as
 * live by older message attachments.
 */
export function closeAskInMessages(
  messages: RobinMessage[],
  askUserToolCallId: string,
  resolvedChoiceId?: string,
): RobinMessage[] {
  return messages.map((message) => {
    const attachment = message.attachment
    if (
      attachment?.type !== "choices" ||
      attachment.live?.askUserToolCallId !== askUserToolCallId
    ) {
      return message
    }

    return {
      ...message,
      attachment: {
        ...attachment,
        resolved: resolvedChoiceId ?? attachment.resolved,
        readOnly: true,
        live: undefined,
      },
    }
  })
}

export function reopenAskInMessages(
  messages: RobinMessage[],
  askUserToolCallId: string,
): RobinMessage[] {
  return messages.map((message) => {
    const attachment = message.attachment
    if (
      attachment?.type !== "choices" ||
      attachment.live?.askUserToolCallId !== askUserToolCallId
    ) {
      return message
    }

    const nextAttachment: RobinChoicesAttachment = {
      type: "choices",
      id: attachment.id,
      options: attachment.options,
      live: attachment.live,
    }
    if (attachment.readOnly) nextAttachment.readOnly = attachment.readOnly

    return {
      ...message,
      attachment: nextAttachment,
    }
  })
}

/**
 * Converts raw composer text into the backend turn contract. Typed text only
 * answers an active candidate/job ask when it exactly matches a visible option;
 * otherwise it remains a normal follow-up and the stale card is suppressed.
 */
export function buildTurnForComposerText(
  text: string,
  activeAsk: PendingAgentAsk | null,
): { turn: AgentConversationTurn; suppressAskId?: string } {
  const body = text.trim()
  if (!activeAsk) return { turn: { kind: "plain_text", body } }

  if (activeAsk.kind === "other" || activeAsk.options.length === 0) {
    return {
      turn: {
        kind: "ask_user_response",
        askUserToolCallId: activeAsk.askUserToolCallId,
        response: { body },
      },
    }
  }

  const match = activeAsk.options.find(
    (option) => normalizeOptionLabel(option.label) === normalizeOptionLabel(body),
  )
  if (match) {
    return {
      turn: {
        kind: "ask_user_response",
        askUserToolCallId: activeAsk.askUserToolCallId,
        response: { selectedIndex: match.index },
      },
    }
  }

  return {
    turn: { kind: "plain_text", body },
    suppressAskId: activeAsk.askUserToolCallId,
  }
}

export function buildTurnForChoiceClick(
  live: LiveContextRef,
  choiceId: string,
): AgentConversationTurn {
  const selectedIndex = Number(choiceId)
  if (!Number.isInteger(selectedIndex)) {
    throw new Error(`Invalid ask-user choice id: ${choiceId}`)
  }
  return {
    kind: "ask_user_response",
    askUserToolCallId: live.askUserToolCallId,
    response: { selectedIndex },
  }
}

export function activeAskFromMessages(
  conversationId: string,
  messages: RobinMessage[],
  createdAtIso: string,
): PendingAgentAsk | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    const attachment = message.attachment
    if (
      attachment?.type !== "choices" ||
      attachment.readOnly ||
      attachment.resolved ||
      !attachment.live
    ) {
      continue
    }

    const live = attachment.live
    return {
      conversationId,
      askUserToolCallId: live.askUserToolCallId,
      botMessageId: message.id,
      kind: live.kind,
      options: attachment.options.map((option) => ({
        index: Number(option.id),
        label: option.name,
        kind: live.kind,
      })),
      createdAt: createdAtIso,
    }
  }
  return null
}

export function mergeUniquePendingAsks(
  asks: Array<PendingAgentAsk | null | undefined>,
): PendingAgentAsk[] {
  const byId = new Map<string, PendingAgentAsk>()
  for (const ask of asks) {
    if (ask) byId.set(ask.askUserToolCallId, ask)
  }
  return [...byId.values()]
}

export function labelFromDate(value: string, nowMs: number): string {
  const then = new Date(value).getTime()
  if (!Number.isFinite(then)) return "now"
  const diffMs = Math.max(0, nowMs - then)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour

  if (diffMs < minute) return "now"
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m`
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h`
  if (diffMs < 2 * day) return "Yesterday"
  return `${Math.floor(diffMs / day)}d`
}

function comparePendingAsk(a: PendingAgentAsk, b: PendingAgentAsk): number {
  const byDate = a.createdAt.localeCompare(b.createdAt)
  return byDate === 0
    ? a.askUserToolCallId.localeCompare(b.askUserToolCallId)
    : byDate
}

function normalizeOptionLabel(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase()
}

function optionKindLabel(kind: AgentAskKind): string {
  switch (kind) {
    case "candidate":
      return "Candidate"
    case "job":
      return "Job"
    case "other":
    default:
      return "Clarification"
  }
}
